import {cdnImage} from '@/util/cdnImage';
import MUITypography from '@/components/MUI-Components/MUI-Typography';
import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableContainer from '@mui/material/TableContainer';
import DisplayImage from '../display-image';
import {borderRadius, colors, fontFamily, fontSize, spacing} from '@/styles/tokens';

const checkTrue = cdnImage('White-Label-Real-Estate-Tokenization/check.webp');
const checkFalse = cdnImage('White-Label-Real-Estate-Tokenization/close.webp');

// Migrated from globals.scss `.blockchain-technology-table .table-name`.
const sxTableName = {
    fontFamily: fontFamily.heading,
    fontSize: fontSize[16],
    paddingTop: spacing[20]
};

// Migrated from globals.scss `.blockchain-technology-table .difference-heading`.
const sxDifferenceHeading = {
    fontFamily: fontFamily.heading
};

// Migrated from globals.scss `.blockchain-technology-table` (+ nested `table`, `td, th`, `th`,
// `td`, `tbody` rules — old lines 2067-2092) and its `mobileScreen` (max-width:430px) `table`
// max-width override (old lines 4887-4891). Sole owner confirmed. Applied to this component's own
// per-item wrapper (converted from a plain `div` to `Box` — same rendered `<div>` tag, zero behavior
// change — so it can take an `sx` prop); the nested `& table`/`& td`/`& th`/`& tbody` descendant
// selectors reproduce the exact original cascade through the intervening TableContainer div, no
// per-node sx needed since MUI's Table/TableCell/TableBody render to native table/td/th/tbody tags.
const blockchainTechnologyTableSx = {
    '& table': {
        fontFamily: 'arial, sans-serif', // no exact token match — preserved for parity
        borderCollapse: 'collapse',
        width: '100%'
    },
    '& td, & th': {
        border: `1px solid ${colors.neutral.borderDim}`,
        textAlign: 'left',
        padding: '20px 15px !important' // no exact token match — preserved for parity
    },
    '& th': {
        backgroundColor: `${colors.primary.main} !important`,
        color: `${colors.text.inverse} !important`,
        fontFamily: `${fontFamily.heading} !important`,
        fontSize: `${fontSize[16]} !important`
    },
    '& td': {
        fontFamily: `${fontFamily.body} !important`,
        fontSize: `${fontSize[16]} !important`
    },
    '& tbody': {
        backgroundColor: '#f1f6ff !important' // no exact token match — preserved for parity
    },
    '@media (max-width: 430px)': {
        '& table': {
            maxWidth: '425px !important'
        }
    }
} as const;

const TableBlockchain = (props: any) => {
    const {data, defaultRow} = props;

    return (
        <div>
            {data?.map((table: any, index: number) => (
                <Box key={index} className="blockchain-technology-table" sx={blockchainTechnologyTableSx}>
                    {table.tableName && <MUITypography sx={sxTableName}>{table.tableName}</MUITypography>}
                    <TableContainer
                        sx={{
                            overflowX: 'auto',
                            '&::-webkit-scrollbar': {
                                height: '6px'
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: colors.neutral.borderLight,
                                borderRadius: borderRadius['10']
                            }
                        }}
                    >
                        <Table>
                            <TableHead>
                                <TableRow>
                                    {table.header?.map((column: string, colIndex: number) => (
                                        <TableCell key={colIndex}>{column}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            {defaultRow ? (
                                <TableBody>
                                    {table?.rows?.map((row: any, rowIndex: number) => (
                                        <TableRow key={rowIndex + 1}>
                                            {row.columns?.map((columnsItem: any, colIndex: number) =>
                                                typeof columnsItem === 'boolean' ? (
                                                    <TableCell key={colIndex + 1}>
                                                        <DisplayImage
                                                            src={columnsItem ? checkTrue : checkFalse}
                                                            alt="checkbox"
                                                            width={24}
                                                        />
                                                    </TableCell>
                                                ) : (
                                                    <TableCell key={colIndex + 1} sx={colIndex === 0 ? sxDifferenceHeading : undefined}>
                                                        {columnsItem}
                                                    </TableCell>
                                                )
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            ) : (
                                <TableBody>
                                    {table?.rows?.map((row: any, rowIndex: number) => (
                                        <TableRow key={rowIndex + 1}>
                                            <TableCell sx={sxDifferenceHeading}>{row.aspect}</TableCell>
                                            <TableCell>{row.centralized}</TableCell>
                                            <TableCell>{row.decentralized}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            )}
                        </Table>
                    </TableContainer>
                </Box>
            ))}
        </div>
    );
};

export default memo(TableBlockchain);
